import { Gift } from 'lucide-react';
import type { FeatureManifest } from '@/app/registry';
import { CONTEXTS, lazyView } from '@/app/registry';
import { requireFeatureEnabled } from '@/app/router/guards/require-feature-enabled';
import { useFeatureFlagsStore } from '@/app/stores/use-feature-flags-store';
import { referralRoutes } from './routes';
import { REFERRAL_PATHS } from './constants';

/**
 * Accès utilisateur ouvert par le drapeau `referral` (SAAS-1162). Sans lui, le
 * parrainage reste coupé (SAAS-1046) : il n'est pas encore ouvert en prod, le
 * drapeau vient de `VITE_FEATURE_FLAGS` au build (staging seulement) ou d'un
 * `setFlag('referral', true)` en console.
 *
 * Le lien nav reste **visible mais grisé** (`disabled` + `disabledTooltip`,
 * cf. `NavigationLink`) plutôt que masqué : demande produit explicite,
 * différente du masquage complet via `enabledWhen` (cf. `admin/copilot`).
 * Le `guard` sur la route est ce qui protège réellement l'accès — une URL
 * `/referral` tapée à la main ne doit pas atteindre `ReferralView` sans le
 * drapeau.
 *
 * Le drapeau est lu **une seule fois, au chargement du module** : le lien
 * (`disabled` est un booléen figé du contrat du registry) et la garde lisent
 * la même constante, ils ne peuvent donc pas se contredire. Le store est déjà
 * rechargé depuis `localStorage` à cet instant (stockage synchrone) : la valeur
 * tient compte du build et des réglages manuels. Un `setFlag` en console
 * demande donc un rechargement de la page.
 */
const REFERRAL_ENABLED = useFeatureFlagsStore.getState().flags.referral === true;

export const referralManifest: FeatureManifest = {
  id: 'referral',
  contexts: [CONTEXTS.REFERRAL],

  routes: [
    {
      zone: 'authenticated',
      routes: referralRoutes,
      errorFallbackLabel: 'Mon parrainage',
      guard: requireFeatureEnabled(REFERRAL_ENABLED),
    },
  ],

  centerView: {
    [CONTEXTS.REFERRAL]: lazyView(() => import('./views/ReferralView'), 'ReferralView'),
  },

  navigation: [
    {
      id: 'referral',
      label: 'Mon parrainage',
      icon: Gift,
      to: REFERRAL_PATHS.root,
      position: 'bottom',
      order: 25,
      disabled: !REFERRAL_ENABLED,
      disabledTooltip: 'Bientôt disponible',
    },
  ],
};
